import { usePricingGuide } from '../../PricingGuide.context';
import type { KnownPrice } from '../../PricingGuide.types';
import { PricingGuideTable } from '../../table/PricingGuideTable';
import { useFeatureFlag } from '@orthly/veneer';
import React from 'react';

type PriceNameMap = {
    flexible: KnownPrice[];
    acetal: KnownPrice[];
    cast_metal: KnownPrice[];
    hybrid: KnownPrice[];
    acrylic_partial: KnownPrice[];
    acrylic_flipper: KnownPrice[];
};

const v1PriceNameMap: PriceNameMap = {
    flexible: ['Flexible Partial (Bilateral, STF)'],
    acetal: ['Flexible Partial (Bilateral, STF)'],
    cast_metal: ['Metal Partial (Bilateral, STF)'],
    hybrid: ['Hybrid Partial (Bilateral, STF)'],
    acrylic_partial: ['Acrylic Partial'],
    acrylic_flipper: ['Acrylic Flipper'],
};

const v2PriceNameMap: PriceNameMap = {
    flexible: [
        '[Practice] Partial Denture - Bilateral - Duraflex - Straight to Finish',
        '[Practice] Partial Denture - Bilateral - Valplast TCS - Straight to Finish',
    ],
    acetal: ['[Practice] Partial Denture - Bilateral - Acetal - Straight to Finish'],
    hybrid: [
        '[Practice] Partial Denture - Bilateral - Chrome Cobalt with Flexible - Tissue Shade Clasps - Straight to Finish',
        '[Practice] Partial Denture - Bilateral - Titanium with Flexible - Tissue Shade Clasps - Straight to Finish',
    ],
    cast_metal: [
        '[Practice] Partial Denture - Bilateral - Chrome Cobalt with Metal Clasps - Straight to Finish',
        '[Practice] Partial Denture - Bilateral - Titanium with Metal Clasps - Straight to Finish',
    ],
    acrylic_partial: ['[Practice] Partial Denture - Acrylic - Straight to Finish - 4+ Teeth'],
    acrylic_flipper: ['[Practice] Partial Denture - Acrylic - Straight to Finish - 1-3 Teeth'],
};

export const PartialDenturesMaterialsTable: React.FC = () => {
    const { formatPrice, getMaxPrice } = usePricingGuide();

    const { value: enablePartialDenturesV2Pricing } = useFeatureFlag('enablePartialDenturesV2PricingGuide');

    const priceNameMap = React.useMemo(
        () => (!!enablePartialDenturesV2Pricing ? v2PriceNameMap : v1PriceNameMap),
        [enablePartialDenturesV2Pricing],
    );

    const rows = React.useMemo(
        () => [
            {
                id: 'flexible',
                material: 'Flexible (Bilateral, Straight to Finish)',
                price: getMaxPrice(priceNameMap['flexible']),
            },
            {
                id: 'acetal',
                material: 'Acetal (Bilateral, Straight to Finish)',
                price: getMaxPrice(priceNameMap['acetal']),
            },
            {
                id: 'cast_metal',
                material: 'Cast Metal (Bilateral, Straight to Finish)',
                price: getMaxPrice(priceNameMap['cast_metal']),
            },
            {
                id: 'hybrid',
                material: 'Hybrid (Bilateral, Straight to Finish)',
                price: getMaxPrice(priceNameMap['hybrid']),
            },
            {
                id: 'acrylic_partial',
                material: 'Acrylic Partial: 4+ teeth',
                price: getMaxPrice(priceNameMap['acrylic_partial']),
            },
            {
                id: 'acrylic_flipper',
                material: 'Acrylic Flipper: 1-3 teeth',
                price: getMaxPrice(priceNameMap['acrylic_flipper']),
            },
        ],
        [getMaxPrice, priceNameMap],
    );

    return (
        <PricingGuideTable
            id={'partial-dentures-materials'}
            title={'Materials'}
            columns={[
                {
                    id: 'material',
                    title: 'Material',
                },
                {
                    id: 'price',
                    title: 'Price',
                    render: ({ price }) => formatPrice(price),
                },
            ]}
            rows={rows}
        />
    );
};
